import { formatRemaining } from "../config/sessionTimeout";
import {
  SESSION_EXPLANATION,
  SESSION_KEEP_ALIVE_HINT,
  SESSION_LABEL,
  SESSION_WARNING_LABEL,
} from "../config/sessionTimeout";
import "./SessionCountdown.css";

/**
 * GMA-1909 — the visible half of the idle-session countdown.
 *
 * Purely presentational: it holds no timer, so a test can render it at any
 * point on the clock.
 *
 * It always SAYS what it is. A bare `30:00` in the corner of a care app is a
 * mystery — the number only means something next to "Auto sign-out in", and the
 * full rule (30 minutes, activity resets it) is on hover and available to
 * screen readers.
 *
 * The warning state is three signals at once — colour, an icon, and changed
 * text. Colour alone fails WCAG 1.4.1, and a caregiver glancing at a tablet
 * across a room is exactly the reader that loses.
 */
const SessionCountdown = ({ remainingMs = null, isWarning = false }) => {
  const known = remainingMs !== null && remainingMs !== undefined;
  // Visual only. The aria-live announcement lives in
  // SessionCountdownAnnouncement, outside the portal this subtree is moved
  // into - see that file for why the two cannot share a node.
  if (!known) {return null;}

  const clock = formatRemaining(remainingMs);
  const label = isWarning ? SESSION_WARNING_LABEL : SESSION_LABEL;

  return (
    <div
      data-testid="session-countdown"
      className={`session-countdown${isWarning ? " session-countdown--warning" : ""}`}
      title={SESSION_EXPLANATION}
    >
      {isWarning && (
        <svg
          data-testid="session-countdown-warning-icon"
          className="session-countdown__icon"
          aria-hidden="true"
          focusable="false"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2 1 21h22L12 2zm1 14h-2v2h2v-2zm0-7h-2v5h2V9z" />
        </svg>
      )}
      <span className="session-countdown__what" data-testid="session-countdown-label">
        {label}
      </span>
      <span
        role="timer"
        data-testid="session-countdown-clock"
        className="session-countdown__clock"
        aria-label={`${label} ${clock}. ${SESSION_EXPLANATION}`}
      >
        {clock}
      </span>
      {/*
        The VISIBLE hint. aria-hidden because the announcement element already
        carries this sentence for screen readers; without this the warning would
        be read twice.
      */}
      {isWarning && (
        <span
          data-testid="session-countdown-hint"
          className="session-countdown__hint"
          aria-hidden="true"
        >
          {SESSION_KEEP_ALIVE_HINT}
        </span>
      )}
    </div>
  );
};

export default SessionCountdown;
